/*  MUI  */
import { Box, Typography } from '@mui/material';

/*  Shared  */
import {
    AppPicker,
    GroupPicker,
    MainCategory,
    UserPicker
} from 'application/Shared/Components/Pickers';
import { FontSizes } from 'application/Shared/Constants';

/*  Dates  */
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ReportSummary = (props) => {
    const {
        expanded: propsExpanded,
        hideReportSelection,
        noDates,
        renderingScheduledReport,
        reportSetup,
        title
    } = props;

    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(propsExpanded || false);

    const { period } = reportSetup;
    const selection = reportSetup.type;

    let start;
    let end;
    if (reportSetup.period === 'weekly') {
        start = startOfWeek(reportSetup.startTime);
        end = endOfWeek(reportSetup.startTime);
    }
    if (reportSetup.period === 'monthly') {
        start = startOfMonth(reportSetup.startTime);
        end = endOfMonth(reportSetup.startTime);
    }
    if (reportSetup.period === 'daily') {
        start = new Date(reportSetup.startTime);
        end = new Date(reportSetup.startTime);
    }
    if (reportSetup.period === 'period') {
        start = new Date(reportSetup.startTime);
        end = new Date(reportSetup.endTime);
    }

    return (
        <Box width="100%" data-trackid="reportSummaryMainRenderContainer">
            <Typography variant="h4">{title}</Typography>
            <Box mt="8px">
                <Typography
                    color="textSecondary"
                    variant="span"
                    style={{ fontWeight: 600 }}
                >
                    {`${t('Common.time_period')}: `}
                </Typography>
                <Typography color="textSecondary" variant="span">
                    {t(`Reports.ProceduralCodes.${period}`)}
                </Typography>
                {!noDates && (
                    <Typography variant="span">
                        &nbsp;({start.toLocaleDateString()} -{' '}
                        {end.toLocaleDateString()})
                    </Typography>
                )}
            </Box>
            <Box>
                <Typography
                    color="textSecondary"
                    variant="span"
                    style={{ fontWeight: 600 }}
                >
                    {`${t('Common.selection')}: `}
                </Typography>
                <Typography variant="span">
                    {t(`Reports.ProceduralCodes.${selection}`)}
                </Typography>
            </Box>
            {!expanded && !renderingScheduledReport && (
                <Box mt="16px">
                    <Box
                        as="a"
                        className="bold link underline"
                        onClick={() => setExpanded(true)}
                    >
                        {t('Reports.ReportSummary.advanced_report_details')}
                    </Box>
                </Box>
            )}
            {(expanded || renderingScheduledReport) && (
                <Box width="100%">
                    {reportSetup.type === 'organizational' &&
                        reportSetup.userOrGroupSelection === 'all' && (
                            <>
                                <Typography
                                    color="textSecondary"
                                    variant="span"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.users')}:{' '}
                                </Typography>
                                {t('Common.all_users')}
                                <br />
                            </>
                        )}
                    {reportSetup.type === 'organizational' &&
                        reportSetup.userOrGroupSelection === 'users' && (
                            <>
                                <Typography
                                    color="textSecondary"
                                    variant="span"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.users')}:{' '}
                                </Typography>
                                <UserPicker
                                    multiple
                                    allValue="All Users"
                                    allValueId="0"
                                    visualOnly
                                    value={reportSetup.userSelection}
                                />
                                <br />
                            </>
                        )}
                    {reportSetup.type === 'organizational' &&
                        reportSetup.userOrGroupSelection === 'groups' && (
                            <>
                                <Typography
                                    color="textSecondary"
                                    variant="span"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.users')}:{' '}
                                </Typography>
                                <GroupPicker
                                    multiple
                                    allValue="All Groups"
                                    allValueId="0"
                                    visualOnly
                                    value={reportSetup.userSelection}
                                />
                                <br />
                            </>
                        )}
                    <>
                        {(reportSetup.applicationOrCategorySelection ===
                            'all' ||
                            reportSetup.applicationOrCategorySelection ===
                                'application') && (
                            <Typography
                                color="textSecondary"
                                variant="p"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: '4px'
                                }}
                            >
                                {t('Common.applications')}:
                            </Typography>
                        )}
                        {reportSetup.applicationOrCategorySelection ===
                            'category' && (
                            <Typography
                                color="textSecondary"
                                variant="p"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: '4px'
                                }}
                            >
                                {t('Common.categories')}:{' '}
                            </Typography>
                        )}
                        {reportSetup.applicationOrCategorySelection ===
                            'all' && (
                            <Typography style={{ fontWeight: 600 }}>
                                All Applications
                            </Typography>
                        )}
                        {reportSetup.applicationOrCategorySelection ===
                            'application' && (
                            <AppPicker
                                multiple
                                allValue={
                                    <Box style={{ fontSize: FontSizes[1] }}>
                                        All Applications
                                    </Box>
                                }
                                allValueId="0"
                                visualOnly
                                value={reportSetup.applicationSelection}
                            />
                        )}
                        {reportSetup.applicationOrCategorySelection ===
                            'category' && (
                            <MainCategory
                                multiple
                                allValue={<Box>All Categories</Box>}
                                allValueId="0"
                                visualOnly
                                value={reportSetup.categorySelection}
                            />
                        )}
                    </>
                    {!renderingScheduledReport && !hideReportSelection && (
                        <>
                            <hr />
                            <Typography
                                color="textSecondary"
                                style={{ fontWeight: 600 }}
                            >
                                {t('Reports.ReportSummary.sub_reports')}:
                            </Typography>
                            {reportSetup.reports.map((report) => (
                                <React.Fragment key={`sr_${report.name}`}>
                                    <Typography
                                        color="textSecondary"
                                        variant="span"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {t(
                                            `Reports.ProceduralCodes.${report.name}`
                                        )}
                                        :
                                    </Typography>
                                    {report.subOptions.map(
                                        (subReport, index) => {
                                            if (!subReport.hidden) {
                                                return (
                                                    <Box
                                                        as="span"
                                                        key={`srn_${subReport.name}`}
                                                    >
                                                        {/* Only add a slash in front of the item if it's not the first one (first item is i === 1) */}
                                                        &nbsp;
                                                        {index !== 0 && (
                                                            <>/&nbsp;</>
                                                        )}
                                                        {t(
                                                            `Reports.ProceduralCodes.${subReport.name}`
                                                        )}
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }
                                    )}
                                    <br />
                                </React.Fragment>
                            ))}
                        </>
                    )}
                </Box>
            )}
            <hr />
        </Box>
    );
};
